import http from './httpClient';

class RecipeLibraryService {
  /**
   * List all recipes in the library
   */
  static async list() {
    const data = await http.get('/recipe-library/list');
    if (!data.success) throw new Error(data.error || 'Failed to list recipes');
    return data.data;
  }

  /**
   * Search recipes by keyword
   */
  static async search(q) {
    if (!q || q.trim() === '') {
      return this.list(); // Return all if no search query
    }
    const data = await http.get(`/recipe-library/search?q=${encodeURIComponent(q)}`);
    if (!data.success) throw new Error(data.error || 'Failed to search library');
    return data.data;
  }

  /**
   * Get recipe details by name with business-specific customizations
   * @param {string} name - Recipe name
   * @param {number} businessId - Business ID (optional, defaults to localStorage)
   */
  static async getRecipe(name, businessId) {
    const bId = businessId || localStorage.getItem('businessId') || null;
    const url = bId 
      ? `/recipe-library/get/${encodeURIComponent(name)}?businessId=${bId}`
      : `/recipe-library/get/${encodeURIComponent(name)}`;
    
    const data = await http.get(url);
    if (!data.success) throw new Error(data.error || 'Recipe not found');
    return data.data;
  }

  /**
   * Import recipe from library into database
   * First tries local JSON library, then falls back to AI if not found
   */
  static async importByName(name, businessId) {
    const data = await http.post('/recipe-library/import', { 
      name,
      businessId: businessId || localStorage.getItem('businessId')
    });
    if (!data.success) throw new Error(data.error || 'Failed to import recipe');
    return data.data;
  }

  /**
   * Get all recipes from library
   */
  static async getAllRecipes() {
    const data = await http.get('/recipe-library/list');
    if (!data.success) throw new Error(data.error || 'Failed to get recipes');
    return data.data;
  }

  /**
   * Update business-specific customizations (add_ons, complimentary_items)
   * @param {string} itemName - Recipe name
   * @param {object} updates - Object containing add_ons and/or complimentary_items
   * @param {number} businessId - Business ID (optional, defaults to localStorage)
   */
  static async updateRecipe(itemName, updates, businessId) {
    const bId = businessId || localStorage.getItem('businessId');
    
    const data = await http.put('/recipe-library/update', {
      item_name: itemName,
      business_id: bId,
      ...updates
    });
    if (!data.success) throw new Error(data.error || 'Failed to update recipe');
    return data.data;
  }

  /**
   * Get recipe formatted for OCR frontend flow (10 plates → 100 plates)
   * @param {string} name - Recipe name
   */
  static async getRecipeForOCR(name) {
    const data = await http.get(`/recipe-library/ocr-format/${encodeURIComponent(name)}`);
    if (!data.success) throw new Error(data.error || 'Recipe not found');
    return data.data;
  }

  /**
   * Save recipe from library to business MenuItems table
   * @param {object} recipeData - Recipe data including ingredients, complimentary_items, add_ons
   */
  static async saveToBusiness(recipeData) {
    const businessId = recipeData.businessId || localStorage.getItem('businessId') || null;
    if (!businessId) {
      throw new Error('Business ID is required');
    }
    
    const data = await http.post('/recipe-library/save-to-business', {
      businessId,
      recipeName: recipeData.name,
      price: recipeData.price || 0,
      servings: recipeData.servings || 1,
      ingredients: recipeData.ingredients || [],  // [{name, unit, quantity}] - per plate
      complimentary_items: recipeData.complimentary_items || [],
      add_ons: recipeData.add_ons || [],
      description: recipeData.description || ''
    });
    
    if (!data.success) throw new Error(data.error || 'Failed to save recipe to business');
    return data.data;
  }
  /**
   * Match business menu items against the recipe library
   * @param {number} businessId - Business ID
   * @returns {Promise<{totalMenuItems, matchCount, matches}>}
   */
  static async matchMenuItems(businessId) {
    const bId = businessId || localStorage.getItem('businessId');
    if (!bId) throw new Error('Business ID is required');

    const data = await http.post('/recipe-library/match-menu-items', { businessId: bId });
    if (!data.success) throw new Error(data.error || 'Failed to match menu items');
    return data;
  }

  /**
   * Bulk sync ingredients from recipe library into business menu items
   * @param {number} businessId
   * @param {Array<{menuItemId, menuItemName}>} selectedItems
   * @param {'replace'|'merge'} conflictStrategy
   */
  static async bulkSync(businessId, selectedItems, conflictStrategy) {
    const bId = businessId || localStorage.getItem('businessId');
    if (!bId) throw new Error('Business ID is required');

    const data = await http.post('/recipe-library/bulk-sync', {
      businessId: bId,
      selectedItems,
      conflictStrategy
    });
    if (!data.success) throw new Error(data.error || 'Failed to bulk sync');
    return data.results;
  }
}

export default RecipeLibraryService;

